import React, { useState } from 'react';
import { authenticateAdmin, createAdminAccount, changePassword } from '../config/adminConfig';

function AdminLogin({ onLogin }) {
  const [view, setView] = useState('login'); // login, create, changePassword
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const sanitizedEmail = email.trim().toLowerCase();
      const sanitizedPassword = password.trim();

      if (!sanitizedEmail || !sanitizedPassword) {
        throw new Error('Email and password are required');
      }

      const isAuthenticated = await authenticateAdmin(sanitizedEmail, sanitizedPassword);

      if (isAuthenticated) {
        localStorage.setItem('adminUser', sanitizedEmail);
        localStorage.setItem('adminLoginTime', Date.now().toString());
        onLogin(sanitizedEmail);
      } else {
        setError('Invalid email or password');
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      if (!email.endsWith('@amazon.com')) {
        throw new Error('Email must be an @amazon.com address');
      }
      if (!password || password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      const result = await createAdminAccount(email.trim(), password);

      if (result.success) {
        setSuccess('Account created! You can now login.');
        setTimeout(() => { setView('login'); setSuccess(''); }, 2000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      if (!email || !password) {
        throw new Error('Email and current password are required');
      }
      if (!newPassword || newPassword.length < 6) {
        throw new Error('New password must be at least 6 characters');
      }
      if (newPassword !== confirmPassword) {
        throw new Error('New passwords do not match');
      }
      if (newPassword === password) {
        throw new Error('New password must be different from current password');
      }

      const result = await changePassword(email.trim(), password, newPassword);

      if (result.success) {
        setSuccess('Password changed! You can now login with your new password.');
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => { setView('login'); setSuccess(''); }, 2000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const formStyle = {
    maxWidth: '400px',
    margin: '80px auto',
    padding: '30px',
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    marginBottom: '12px',
    boxSizing: 'border-box'
  };

  const btnPrimary = {
    width: '100%',
    padding: '12px',
    background: '#ff9900',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '10px'
  };

  const btnSecondary = {
    width: '100%',
    padding: '10px',
    background: 'transparent',
    color: '#007bff',
    border: '1px solid #007bff',
    borderRadius: '4px',
    fontSize: '13px',
    cursor: 'pointer',
    marginBottom: '6px'
  };

  // Change Password View
  if (view === 'changePassword') {
    return (
      <div style={formStyle}>
        <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>🔑 Change Password</h2>
        <form onSubmit={handleChangePassword}>
          <label style={{ fontSize: '13px', fontWeight: '500' }}>Email:</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="youralias@amazon.com" required style={inputStyle} />
          
          <label style={{ fontSize: '13px', fontWeight: '500' }}>Current Password:</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
          
          <label style={{ fontSize: '13px', fontWeight: '500' }}>New Password:</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Min 6 characters" required style={inputStyle} />
          
          <label style={{ fontSize: '13px', fontWeight: '500' }}>Confirm New Password:</label>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required style={inputStyle} />

          {error && <div style={{ color: '#dc3545', background: '#f8d7da', padding: '10px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}
          {success && <div style={{ color: '#155724', background: '#d4edda', padding: '10px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>{success}</div>}

          <button type="submit" disabled={isLoading} style={btnPrimary}>
            {isLoading ? 'Changing...' : 'Change Password'}
          </button>
          <button type="button" onClick={() => { setView('login'); setError(''); setSuccess(''); }} style={btnSecondary}>
            Back to Login
          </button>
        </form>
      </div>
    );
  }

  // Create Account View
  if (view === 'create') {
    return (
      <div style={formStyle}>
        <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>Create Admin Account</h2>
        <form onSubmit={handleCreateAccount}>
          <label style={{ fontSize: '13px', fontWeight: '500' }}>Amazon Email:</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="youralias@amazon.com" required style={inputStyle} />
          
          <label style={{ fontSize: '13px', fontWeight: '500' }}>Password:</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" required style={inputStyle} />
          
          <label style={{ fontSize: '13px', fontWeight: '500' }}>Confirm Password:</label>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required style={inputStyle} />

          {error && <div style={{ color: '#dc3545', background: '#f8d7da', padding: '10px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}
          {success && <div style={{ color: '#155724', background: '#d4edda', padding: '10px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>{success}</div>}

          <button type="submit" disabled={isLoading} style={btnPrimary}>
            {isLoading ? 'Creating...' : 'Create Account'}
          </button>
          <button type="button" onClick={() => { setView('login'); setError(''); setSuccess(''); }} style={btnSecondary}>
            Back to Login
          </button>
        </form>
      </div>
    );
  }

  // Login View
  return (
    <div style={formStyle}>
      <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>📸 Photo Studio Admin</h2>
      <form onSubmit={handleLogin}>
        <label style={{ fontSize: '13px', fontWeight: '500' }}>Email:</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@amazon.com" required style={inputStyle} />
        
        <label style={{ fontSize: '13px', fontWeight: '500' }}>Password:</label>
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />

        {error && <div style={{ color: '#dc3545', background: '#f8d7da', padding: '10px', borderRadius: '4px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

        <button type="submit" disabled={isLoading} style={btnPrimary}>
          {isLoading ? 'Logging in...' : 'Login'}
        </button>
        <button type="button" onClick={() => { setView('create'); setError(''); }} style={btnSecondary}>
          Create New Account
        </button>
        <button type="button" onClick={() => { setView('changePassword'); setError(''); }} style={{...btnSecondary, color: '#6c757d', borderColor: '#6c757d'}}>
          Forgot / Change Password
        </button>
      </form>
    </div>
  );
}

export default AdminLogin;
